/** One step of an alignment: a pair that matches, or a word on one side only. */
export type Op = { kind: 'same'; i: number; j: number } | { kind: 'del'; i: number } | { kind: 'ins'; j: number };

/**
 * Align two word lists by their longest common subsequence.
 *
 * Out of `transcript.ts`'s `narrow`, so say-it-back (say-it-back §4.5) and
 * recast marking share one walk rather than two that could disagree about
 * which word paired with which. The equality is the caller's: recasts compare
 * lowercased words, an attempt also ignores the punctuation STT adds.
 *
 * The table is walked into a flat list before any grouping — grouping in the
 * same loop is what `narrow`'s first version got wrong, overshooting a run
 * and pairing the wrong words.
 */
export function diffOps<T>(a: readonly T[], b: readonly T[], same: (x: T, y: T) => boolean): Op[] {
  const n = a.length;
  const k = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(k + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = k - 1; j >= 0; j--) {
      dp[i][j] = same(a[i], b[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < n || j < k) {
    if (i < n && j < k && same(a[i], b[j])) {
      ops.push({ kind: 'same', i, j });
      i++;
      j++;
    } else if (j >= k || (i < n && dp[i + 1][j] >= dp[i][j + 1])) {
      ops.push({ kind: 'del', i });
      i++;
    } else {
      ops.push({ kind: 'ins', j });
      j++;
    }
  }
  return ops;
}
